// utils/geoUtils.js
import { ROUTE_CONFIG } from '../config/routeConfig';

/**
 * Calculate distance between two points using Haversine formula
 * @returns distance in miles
 */
export const calculateDistance = (point1, point2) => {
  const lat1 = point1.latitude || point1.lat || point1.location?.coordinates?.[1];
  const lng1 = point1.longitude || point1.lng || point1.location?.coordinates?.[0];
  const lat2 = point2.latitude || point2.lat || point2.location?.coordinates?.[1];
  const lng2 = point2.longitude || point2.lng || point2.location?.coordinates?.[0];
  
  if (!lat1 || !lng1 || !lat2 || !lng2) return null;
  
  const R = 3959; // Earth's radius in miles
  const dLat = (lat2 - lat1) * Math.PI / 180;
  const dLng = (lng2 - lng1) * Math.PI / 180;
  
  const a = 
    Math.sin(dLat/2) * Math.sin(dLat/2) +
    Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) *
    Math.sin(dLng/2) * Math.sin(dLng/2);
    
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a));
  return R * c;
};

/**
 * Format distance for display
 */
export const formatDistance = (miles) => {
  if (miles === null || miles === undefined) return 'N/A';
  
  if (miles < 0.1) {
    return `${Math.round(miles * 5280)} ft`;
  } else if (miles < 10) {
    return `${miles.toFixed(1)} mi`;
  } else {
    return `${Math.round(miles)} mi`;
  }
};

/**
 * Get coordinates from various object formats
 */
export const extractCoordinates = (location) => {
  if (!location) return null;
  
  // Try different formats
  if (location.latitude && location.longitude) {
    return {
      lat: parseFloat(location.latitude),
      lng: parseFloat(location.longitude)
    };
  }
  
  if (location.lat && location.lng) {
    return {
      lat: parseFloat(location.lat),
      lng: parseFloat(location.lng)
    };
  }
  
  if (location.location?.coordinates?.length === 2) {
    return {
      lat: parseFloat(location.location.coordinates[1]),
      lng: parseFloat(location.location.coordinates[0])
    };
  }
  
  if (Array.isArray(location) && location.length === 2) {
    return {
      lat: parseFloat(location[1]),
      lng: parseFloat(location[0])
    };
  }
  
  return null;
};

/**
 * Convert coordinates to Google Maps LatLng object
 */
export const toGoogleLatLng = (coords) => {
  const extracted = extractCoordinates(coords);
  if (!extracted || !window.google) return null;
  
  return new window.google.maps.LatLng(extracted.lat, extracted.lng);
};

/**
 * Calculate bounds for multiple markers
 */
export const calculateBounds = (markers) => {
  if (!markers || markers.length === 0 || !window.google) return null;
  
  const bounds = new window.google.maps.LatLngBounds();
  
  markers.forEach(marker => {
    const coords = extractCoordinates(marker);
    if (coords) {
      bounds.extend(new window.google.maps.LatLng(coords.lat, coords.lng));
    }
  });
  
  return bounds;
};

/**
 * Calculate center point of multiple markers
 */
export const calculateCenter = (markers) => {
  if (!markers || markers.length === 0) {
    return ROUTE_CONFIG.MAP.DEFAULT_CENTER;
  }
  
  let totalLat = 0;
  let totalLng = 0;
  let count = 0;
  
  markers.forEach(marker => {
    const coords = extractCoordinates(marker);
    if (coords) {
      totalLat += coords.lat;
      totalLng += coords.lng;
      count++;
    }
  });
  
  if (count === 0) {
    return ROUTE_CONFIG.MAP.DEFAULT_CENTER;
  }
  
  return {
    lat: totalLat / count,
    lng: totalLng / count
  };
};

/**
 * Calculate optimal zoom level for bounds
 */
export const calculateZoomLevel = (bounds, mapDimensions) => {
  if (!bounds || !window.google) {
    return ROUTE_CONFIG.MAP.DEFAULT_ZOOM;
  }
  
  const WORLD_DIM = { height: 256, width: 256 };
  const ZOOM_MAX = 21;
  
  const ne = bounds.getNorthEast();
  const sw = bounds.getSouthWest();
  
  const latDiff = Math.abs(ne.lat() - sw.lat());
  const lngDiff = Math.abs(ne.lng() - sw.lng());
  
  const latZoom = Math.floor(Math.log2(mapDimensions.height * 360 / latDiff / WORLD_DIM.height));
  const lngZoom = Math.floor(Math.log2(mapDimensions.width * 360 / lngDiff / WORLD_DIM.width));
  
  const zoom = Math.min(latZoom, lngZoom, ZOOM_MAX);
  
  return Math.max(1, zoom - 1); // Subtract 1 for padding
};

/**
 * Create marker icon configuration
 */
export const createMarkerIcon = (type, color, scale) => {
  if (!window.google) return null;
  
  const configs = {
    circle: {
      path: window.google.maps.SymbolPath.CIRCLE,
      scale: scale || ROUTE_CONFIG.MAP.MARKER.SITE_CIRCLE_SCALE,
      fillColor: color,
      fillOpacity: 0.8,
      strokeWeight: 2,
      strokeColor: 'white'
    },
    arrow: {
      path: window.google.maps.SymbolPath.FORWARD_CLOSED_ARROW,
      scale: scale || ROUTE_CONFIG.MAP.MARKER.CREW_ARROW_SCALE,
      fillColor: color,
      fillOpacity: 0.8,
      strokeWeight: 2,
      rotation: 0,
      strokeColor: color
    },
    pin: {
      path: 'M 12,2 C 8.1340068,2 5,5.1340068 5,9 c 0,5.25 7,13 7,13 0,0 7,-7.75 7,-13 0,-3.8659932 -3.134007,-7 -7,-7 z',
      anchor: new window.google.maps.Point(12, 17),
      fillOpacity: 1,
      fillColor: color,
      strokeWeight: 3,
      strokeColor: 'yellow',
      scale: scale || 2,
      labelOrigin: new window.google.maps.Point(12, 9),
    }
  };
  
  return configs[type] || configs.circle;
};

/**
 * Check if coordinates are within service area
 */
export const isInServiceArea = (coords) => {
  const point = extractCoordinates(coords);
  if (!point) return false;
  
  // Arizona rough bounds
  const bounds = {
    north: 37,
    south: 31,
    east: -109,
    west: -115
  };
  
  return point.lat >= bounds.south && 
         point.lat <= bounds.north && 
         point.lng >= bounds.west && 
         point.lng <= bounds.east;
};

/**
 * Calculate driving time estimate (rough approximation)
 * @returns time in minutes
 */
export const estimateDrivingTime = (distanceMiles) => {
  if (!distanceMiles) return 0;
  
  // Assume average speed of 30 mph in city
  const avgSpeedMph = 30;
  return Math.ceil((distanceMiles / avgSpeedMph) * 60);
};

/**
 * Get map offset based on view type
 */
export const getMapOffset = (hasSelectedCrew) => {
  return hasSelectedCrew ? 
    ROUTE_CONFIG.MAP.OFFSET : 
    ROUTE_CONFIG.MAP.OFFSET_UNASSIGNED;
};

/**
 * Adjust center for UI offset
 */
export const adjustCenterForOffset = (center, offset) => {
  return {
    lat: center.lat - offset,
    lng: center.lng
  };
};

export const normalizedMarkerPosition = (marker) => {
  if (!marker || (!marker.location && (!marker?.latitude || !marker?.longitude))) {
    console.log('This one is fucked...', marker)
    return null
  }
  if (marker.location) {
    if (marker.location.type && marker.location.coordinates?.length) {
      return { lat: marker.location.coordinates[1], lng: marker.location.coordinates[0] }
    } else if (marker.location.length === 2) {
      return { lat: parseFloat(marker.location[1]), lng: parseFloat(marker.location[0]) }
    }
    return null
  } else if (marker.latitude && marker.longitude) {
    return { lat: parseFloat(marker.latitude), lng: parseFloat(marker.longitude) }
  }
  return null
}